/**
 * Features de voz en cliente (Web Audio). No graba ni sube audio.
 * Solo índices numéricos para Estado Funcional (cambio, no veracidad).
 */

function mean(arr) {
    if (!arr.length) return 0;
    return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function std(arr) {
    if (arr.length < 2) return 0;
    const m = mean(arr);
    return Math.sqrt(mean(arr.map((x) => (x - m) ** 2)));
}

/**
 * Captura ~durationMs de micrófono, calcula índices, cierra tracks.
 * @returns {Promise<{ indices: object, quality: object, vector: number[] }>}
 */
export async function captureVoiceIndices({ durationMs = 8000, scriptId = 'ef-voice-es-co-v1' } = {}) {
    if (!navigator.mediaDevices?.getUserMedia) {
        const err = new Error('Micrófono no disponible en este dispositivo');
        err.code = 'VOICE_UNSUPPORTED';
        throw err;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
    });

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    const timeData = new Uint8Array(analyser.fftSize);
    const freqData = new Uint8Array(analyser.frequencyBinCount);
    const rmsSamples = [];
    const centroidSamples = [];
    const zcrSamples = [];

    const started = performance.now();
    await new Promise((resolve) => {
        const tick = () => {
            analyser.getByteTimeDomainData(timeData);
            analyser.getByteFrequencyData(freqData);

            let sumSq = 0;
            let zcr = 0;
            let prev = timeData[0] - 128;
            for (let i = 0; i < timeData.length; i++) {
                const v = (timeData[i] - 128) / 128;
                sumSq += v * v;
                const cur = timeData[i] - 128;
                if ((prev >= 0 && cur < 0) || (prev < 0 && cur >= 0)) zcr += 1;
                prev = cur;
            }
            const rms = Math.sqrt(sumSq / timeData.length);
            rmsSamples.push(rms);
            zcrSamples.push(zcr / timeData.length);

            let num = 0;
            let den = 0;
            for (let i = 0; i < freqData.length; i++) {
                const mag = freqData[i];
                num += i * mag;
                den += mag;
            }
            centroidSamples.push(den > 0 ? num / den : 0);

            if (performance.now() - started >= durationMs) resolve();
            else requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    });

    stream.getTracks().forEach((t) => t.stop());
    try {
        await ctx.close();
    } catch {
        /* ignore */
    }

    const rmsMean = mean(rmsSamples);
    const rmsStd = std(rmsSamples);
    const centroidMean = mean(centroidSamples);
    const zcrMean = mean(zcrSamples);
    const voicedRatio = rmsSamples.filter((r) => r > 0.02).length / Math.max(1, rmsSamples.length);

    let shimmerAcc = 0;
    let shimmerN = 0;
    for (let i = 1; i < rmsSamples.length; i++) {
        const a = rmsSamples[i - 1];
        const b = rmsSamples[i];
        const den = (a + b) / 2;
        if (den > 0.005) {
            shimmerAcc += Math.abs(a - b) / den;
            shimmerN += 1;
        }
    }
    const shimmerProxy = shimmerN ? shimmerAcc / shimmerN : 0;
    const intensity = rmsMean;
    const depth = round4(1 / (1 + centroidMean / 40));

    const indices = {
        rmsMean: round4(rmsMean),
        rmsStd: round4(rmsStd),
        spectralCentroidMean: round4(centroidMean),
        zcrMean: round4(zcrMean),
        voicedRatio: round4(voicedRatio),
        intensity: round4(intensity),
        depth,
        shimmerProxy: round4(shimmerProxy),
        durationMs,
        scriptId,
    };

    const vector = [
        indices.rmsMean,
        indices.rmsStd,
        indices.spectralCentroidMean / 100,
        indices.zcrMean,
        indices.voicedRatio,
    ];

    const quality = {
        sampleCount: rmsSamples.length,
        snrProxy: round4(rmsMean / (rmsStd + 1e-6)),
        voicedRatio: indices.voicedRatio,
        ok: voicedRatio >= 0.15 && rmsMean >= 0.01,
    };

    return { indices, quality, vector };
}

function round4(n) {
    return Math.round(Number(n) * 10000) / 10000;
}

/** Texto neutro de lectura (es-CO) — no inducir emoción clínica. */
export const EF_VOICE_SCRIPT_ES = {
    id: 'ef-voice-es-co-v1',
    title: 'Lectura breve',
    secondsHint: 8,
    text:
        'En el trabajo cuidamos el ritmo del equipo. Hoy reviso con calma cómo llego al turno, ' +
        'qué necesito para cumplir con seguridad, y cómo puedo apoyar a quienes trabajan a mi lado. ' +
        'Uno, dos, tres: respirar y continuar.',
};
